import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useData } from "@/lib/dataStore";
import { Receipt as ReceiptIcon, ChartBar, ChartLineUp, SignOut, Leaf, Gear, ReceiptX, Lock, Bank } from "@phosphor-icons/react";
import SettingsDialog from "@/pages/SettingsDialog";

const TABS = [
  { to: "/receipt", label: "Invoice", Icon: ReceiptX, testId: "nav-tab-receipt", color: "blue" },
  { to: "/logs", label: "Logs", Icon: ReceiptIcon, testId: "nav-tab-logs", color: "amber" },
  { to: "/tax", label: "Tax", Icon: ChartBar, testId: "nav-tab-tax", color: "red" },
  { to: "/profit", label: "P&L", Icon: ChartLineUp, testId: "nav-tab-profit", color: "emerald" },
  { to: "/ledger", label: "Ledger", Icon: Bank, testId: "nav-tab-ledger", color: "indigo" },
];

// Per-tab color palette: pill background, text & border for the inactive (default) state
// and a stronger filled variant for active. Kept inline so Tailwind JIT picks them up.
const TAB_COLORS = {
  blue:    { idle: "bg-blue-50 text-blue-800 border-blue-200 hover:bg-blue-100",            active: "bg-blue-600 text-white border-blue-700 shadow-sm" },
  amber:   { idle: "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100",        active: "bg-amber-600 text-white border-amber-700 shadow-sm" },
  red:     { idle: "bg-red-50 text-red-800 border-red-200 hover:bg-red-100",                active: "bg-red-600 text-white border-red-700 shadow-sm" },
  emerald: { idle: "bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100", active: "bg-emerald-700 text-white border-emerald-800 shadow-sm" },
  indigo:  { idle: "bg-indigo-50 text-indigo-800 border-indigo-200 hover:bg-indigo-100",    active: "bg-indigo-600 text-white border-indigo-700 shadow-sm" },
};

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { settings } = useData();
  const prodAt = settings?.production_started_at;

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-slate-200" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        {/* Brand row */}
        <div className="max-w-5xl mx-auto px-4 md:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="relative shrink-0">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-700 to-emerald-900 flex items-center justify-center shadow-sm">
                <Leaf size={20} weight="duotone" color="white" />
              </div>
              <div className="absolute -bottom-1 -right-1 bg-white text-emerald-900 text-[8px] font-extrabold tracking-wider px-1 rounded-md border border-emerald-200 leading-[10px]">TNL</div>
            </div>
            <div className="leading-tight min-w-0">
              <div className="font-heading font-extrabold tracking-tight text-slate-900 text-base sm:text-lg truncate">The NordHim Ledger</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-800/80 font-semibold hidden sm:block">by Sukesh Barua</div>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={() => setSettingsOpen(true)}
              data-testid="settings-open-button"
              className="text-slate-600 hover:text-emerald-800 transition-colors"
              aria-label="Settings"
            >
              <Gear size={22} weight="duotone" />
            </button>
            <button
              onClick={doLogout}
              data-testid="logout-button"
              className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-red-700 transition-colors"
            >
              <span className="hidden md:inline truncate max-w-[200px]">{user?.email}</span>
              <SignOut size={20} weight="duotone" />
            </button>
          </div>
        </div>
        {/* Pre-production TEST-mode banner — only visible until master clicks
            "Mark today as start of production" in Settings */}
        {!prodAt && (
          <div className="bg-amber-100 border-y border-amber-300 px-4 py-1.5" data-testid="pre-production-banner">
            <div className="max-w-5xl mx-auto flex items-center justify-center gap-2 text-[11px] font-bold tracking-wider uppercase text-amber-900">
              <span className="inline-block h-2 w-2 rounded-full bg-amber-600 animate-pulse" />
              Pre-Production · TEST MODE — Customer names are auto-tagged &ldquo;… TEST&rdquo;
            </div>
          </div>
        )}
        {/* Top navigation tabs — all on one row, encapsulated in colored pills */}
        <nav className="border-t border-slate-100" data-testid="top-nav">
          <div className="max-w-5xl mx-auto px-2 sm:px-4 py-2 flex items-center justify-between gap-1.5 sm:gap-2 overflow-x-auto no-scrollbar">
            {TABS.map(({ to, label, Icon, testId, color }) => {
              const palette = TAB_COLORS[color] || TAB_COLORS.emerald;
              return (
                <NavLink
                  key={to}
                  to={to}
                  data-testid={testId}
                  className={({ isActive }) =>
                    `flex-1 min-w-0 inline-flex items-center justify-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-2 rounded-lg border text-[11px] sm:text-sm font-semibold tracking-wide transition-all whitespace-nowrap ${
                      isActive ? palette.active : palette.idle
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon size={16} weight={isActive ? "fill" : "duotone"} className="shrink-0" />
                      <span className="truncate">{label}</span>
                    </>
                  )}
                </NavLink>
              );
            })}
          </div>
        </nav>
      </header>

      <main className="flex-1 max-w-5xl w-full mx-auto px-4 md:px-8 py-6">
        {user?.role === "viewer" && (
          <div className="mb-4 flex items-center gap-2 rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-900" data-testid="viewer-mode-banner">
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold bg-blue-700 text-white px-1.5 py-0.5 rounded">Demo</span>
            <span>Read-only mode — you can view all data but cannot make changes.</span>
          </div>
        )}
        {prodAt && (
          <div className="mb-4 flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-900" data-testid="production-lock-banner">
            <Lock size={14} weight="fill" className="text-emerald-800" />
            <span>
              Production active since <span className="font-semibold">{prodAt}</span> · records immutable until <span className="font-semibold">{settings?.retention_required_until || "—"}</span>
            </span>
          </div>
        )}
        <Outlet />
      </main>

      {/* Page footer — leaves room (right-side margin) so it doesn't sit under the Emergent badge */}
      <footer
        className="mt-4 border-t border-slate-200 bg-white"
        data-testid="app-footer"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-[11px] text-slate-500">
          <div className="flex items-center gap-1.5">
            <Leaf size={13} weight="duotone" className="text-emerald-800" />
            <span>© {new Date().getFullYear()} <span className="font-semibold text-slate-700">Sukesh Barua</span> · The NordHim Ledger (TNL)</span>
          </div>
          <div className="flex items-center gap-1.5 sm:pr-44">
            <span>Made with</span>
            <a
              href="https://emergent.sh"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-slate-700 hover:text-emerald-800 transition-colors"
              data-testid="footer-emergent-link"
            >
              Emergent
            </a>
          </div>
        </div>
      </footer>

      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
    </div>
  );
}
